import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Alert,
  Snackbar,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import DataTable, { Column } from '../../components/common/DataTable';
import StatusChip from '../../components/common/StatusChip';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import {
  getBlacklist,
  createBlacklist,
  updateBlacklist,
  deleteBlacklist,
} from '../../services/api/securityApi';
import { MalwareBlacklist, BlacklistFormData } from '../../types/analysis';
import { formatDateTime } from '../../utils/formatters';

const riskLevelLabels: Record<string, string> = {
  low: '낮음',
  medium: '중간',
  high: '높음',
  critical: '심각',
};

const BlacklistPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<{
    page: number;
    limit: number;
  }>({
    page: 0,
    limit: 20,
  });
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<MalwareBlacklist | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    itemId: number | null;
  }>({
    open: false,
    itemId: null,
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const { control, handleSubmit, reset, formState: { errors } } = useForm<BlacklistFormData>({
    defaultValues: {
      packageName: '',
      sha256: '',
      appName: '',
      threatType: '',
      riskLevel: 'medium',
      description: '',
      source: '',
    },
  });

  const { data, isLoading } = useQuery({
    queryKey: ['blacklist', filters],
    queryFn: () => getBlacklist(filters),
  });

  const createMutation = useMutation({
    mutationFn: createBlacklist,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blacklist'] });
      setSnackbar({
        open: true,
        message: '블랙리스트가 추가되었습니다.',
        severity: 'success',
      });
      handleCloseFormDialog();
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.message || '블랙리스트 추가에 실패했습니다.',
        severity: 'error',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<BlacklistFormData> }) =>
      updateBlacklist(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blacklist'] });
      setSnackbar({
        open: true,
        message: '블랙리스트가 수정되었습니다.',
        severity: 'success',
      });
      handleCloseFormDialog();
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.message || '블랙리스트 수정에 실패했습니다.',
        severity: 'error',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteBlacklist,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blacklist'] });
      setSnackbar({
        open: true,
        message: '블랙리스트가 삭제되었습니다.',
        severity: 'success',
      });
      setConfirmDialog({ open: false, itemId: null });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.message || '블랙리스트 삭제에 실패했습니다.',
        severity: 'error',
      });
    },
  });

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleRowsPerPageChange = (limit: number) => {
    setFilters(prev => ({ ...prev, limit, page: 0 }));
  };

  const handleOpenAddDialog = () => {
    setEditingItem(null);
    reset({
      packageName: '',
      sha256: '',
      appName: '',
      threatType: '',
      riskLevel: 'medium',
      description: '',
      source: '',
    });
    setFormDialogOpen(true);
  };

  const handleOpenEditDialog = (item: MalwareBlacklist) => {
    setEditingItem(item);
    reset({
      packageName: item.packageName || '',
      sha256: item.sha256 || '',
      appName: item.appName || '',
      threatType: item.threatType,
      riskLevel: item.riskLevel,
      description: item.description || '',
      source: item.source || '',
    });
    setFormDialogOpen(true);
  };

  const handleCloseFormDialog = () => {
    setFormDialogOpen(false);
    setEditingItem(null);
    reset();
  };

  const handleFormSubmit = (data: BlacklistFormData) => {
    if (editingItem) {
      updateMutation.mutate({ id: editingItem.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDeleteClick = (id: number) => {
    setConfirmDialog({ open: true, itemId: id });
  };

  const handleConfirmDelete = () => {
    if (confirmDialog.itemId) {
      deleteMutation.mutate(confirmDialog.itemId);
    }
  };

  const handleCancelDelete = () => {
    setConfirmDialog({ open: false, itemId: null });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const columns: Column<MalwareBlacklist>[] = [
    {
      id: 'appName',
      label: '앱 이름',
      minWidth: 150,
    },
    {
      id: 'packageName',
      label: '패키지명',
      minWidth: 200,
      format: (value: string | null) => value || '-',
    },
    {
      id: 'threatType',
      label: '위협 유형',
      minWidth: 120,
    },
    {
      id: 'riskLevel',
      label: '위험도',
      minWidth: 100,
      align: 'center',
      format: (value: string) => (
        <StatusChip status={value} label={riskLevelLabels[value] || value} />
      ),
    },
    {
      id: 'isActive',
      label: '상태',
      minWidth: 100,
      align: 'center',
      format: (value: boolean) => (
        <StatusChip status={value ? 'active' : 'inactive'} label={value ? '활성' : '비활성'} />
      ),
    },
    {
      id: 'detectionDate',
      label: '탐지일',
      minWidth: 150,
      format: (value: Date) => formatDateTime(value),
    },
    {
      id: 'actions',
      label: '작업',
      minWidth: 150,
      align: 'center',
      format: (_value: any, row: MalwareBlacklist) => (
        <Stack direction="row" spacing={1} justifyContent="center">
          <Button size="small" variant="outlined" onClick={() => handleOpenEditDialog(row)}>
            수정
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={() => handleDeleteClick(row.id)}
          >
            삭제
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">악성 앱 블랙리스트 관리</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAddDialog}>
          블랙리스트 추가
        </Button>
      </Box>

      <DataTable
        columns={columns}
        data={data?.items || []}
        total={data?.total || 0}
        page={filters.page}
        rowsPerPage={filters.limit}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        loading={isLoading}
      />

      {/* 추가/수정 폼 다이얼로그 */}
      <Dialog open={formDialogOpen} onClose={handleCloseFormDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? '블랙리스트 수정' : '블랙리스트 추가'}</DialogTitle>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <DialogContent>
            <Stack spacing={2}>
              <Controller
                name="appName"
                control={control}
                rules={{ required: '앱 이름은 필수입니다' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="앱 이름"
                    fullWidth
                    error={!!errors.appName}
                    helperText={errors.appName?.message}
                  />
                )}
              />
              <Controller
                name="packageName"
                control={control}
                render={({ field }) => <TextField {...field} label="패키지명" fullWidth />}
              />
              <Controller
                name="sha256"
                control={control}
                render={({ field }) => <TextField {...field} label="SHA256" fullWidth />}
              />
              <Controller
                name="threatType"
                control={control}
                rules={{ required: '위협 유형은 필수입니다' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="위협 유형"
                    fullWidth
                    error={!!errors.threatType}
                    helperText={errors.threatType?.message}
                  />
                )}
              />
              <Controller
                name="riskLevel"
                control={control}
                rules={{ required: '위험도는 필수입니다' }}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.riskLevel}>
                    <InputLabel>위험도</InputLabel>
                    <Select {...field} label="위험도">
                      <MenuItem value="low">낮음</MenuItem>
                      <MenuItem value="medium">중간</MenuItem>
                      <MenuItem value="high">높음</MenuItem>
                      <MenuItem value="critical">심각</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="설명" fullWidth multiline rows={3} />
                )}
              />
              <Controller
                name="source"
                control={control}
                render={({ field }) => <TextField {...field} label="출처" fullWidth />}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseFormDialog}>취소</Button>
            <Button type="submit" variant="contained">
              {editingItem ? '수정' : '추가'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* 삭제 확인 다이얼로그 */}
      <ConfirmDialog
        open={confirmDialog.open}
        title="블랙리스트 삭제"
        message="이 블랙리스트 항목을 삭제하시겠습니까?"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        confirmText="삭제"
        confirmColor="error"
      />

      {/* 스낵바 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BlacklistPage;
